import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileDown, Sparkles, Loader2, Printer, Save, LogOut, ShieldAlert, Send, Wallet } from "lucide-react";
import { useAuth } from "@/lib/auth";
import DischargeChecklist, { CHECKLIST_ITEMS } from "@/components/patient/discharge/DischargeChecklist";
import LamaForm from "@/components/patient/discharge/LamaForm";
import ReferralForm from "@/components/patient/discharge/ReferralForm";
import { useDischargePdf } from "@/components/patient/discharge/useDischargePdf";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));

const ROUTINE_FIELDS = [
  ["final_diagnosis", "Final diagnosis"],
  ["hospital_course", "Hospital course"],
  ["treatment_given", "Treatment given"],
  ["feeding", "Feeding at discharge"],
  ["medications_on_discharge", "Medications on discharge"],
  ["condition_at_discharge", "Condition at discharge"],
  ["vaccination", "Vaccination status"],
  ["follow_up", "Follow-up plan"],
];

const TYPES = [
  { key: "routine",  label: "Routine discharge", Icon: LogOut,      accent: "primary" },
  { key: "lama",     label: "LAMA",              Icon: ShieldAlert, accent: "rose" },
  { key: "referral", label: "Refer to higher centre", Icon: Send,   accent: "amber" },
];

function mergeAiDraft(prev, draft) {
  const next = { ...prev };
  for (const [k] of ROUTINE_FIELDS) {
    const existing = (prev[k] || "").trim();
    if (!existing) next[k] = draft[k] || "";
    else next[k] = (prev[k] + "\n\n— AI draft —\n" + (draft[k] || "")).trim();
  }
  return next;
}

export default function DischargeTab({ patient, onDischarged }) {
  const { user } = useAuth();
  const [f, setF] = useState({ discharge_type: "routine" });
  const [checklist, setChecklist] = useState({});
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [weight, setWeight] = useState(patient.current_weight_g || "");
  const [dirty, setDirty] = useState(false);
  const [billing, setBilling] = useState(null);   // { totals, bill, settings }

  useEffect(() => {
    api.get(`/patients/${patient.id}/discharge`).then(({ data }) => {
      setF({ discharge_type: "routine", ...(data || {}) });
      setChecklist(data?.checklist || {});
      if (data?.weight_at_discharge_g) setWeight(data.weight_at_discharge_g);
      setDirty(false);
    }).catch(() => {});
  }, [patient.id]);

  const loadBilling = useCallback(async () => {
    try {
      const [{ data: bill }, { data: settings }] = await Promise.all([
        api.get(`/billing/bills/${patient.id}`),
        api.get(`/billing/settings`).catch(() => ({ data: {} })),
      ]);
      setBilling({ ...bill, settings });
    } catch (err) {
      // Not fatal — the billing card is optional (offline, permission, no bill yet).
      // Log for debuggability, keep the UI silent.
      // eslint-disable-next-line no-console
      console.debug("Discharge billing summary unavailable:", err?.response?.status || err?.message);
    }
  }, [patient.id]);

  useEffect(() => { loadBilling(); }, [loadBilling]);

  const updateField = (k, v) => { setF((s) => ({ ...s, [k]: v })); setDirty(true); };
  const toggleCheck = (k) => { setChecklist((s) => ({ ...s, [k]: !s[k] })); setDirty(true); };
  const changeType = (t) => { setF((s) => ({ ...s, discharge_type: t })); setDirty(true); };

  const type = f.discharge_type || "routine";
  const isRoutine = type === "routine";
  const ready = CHECKLIST_ITEMS.every(([k]) => checklist[k]);

  const persist = useCallback(async ({ finalize = false } = {}) => {
    setSaving(true);
    try {
      const payload = { ...f, checklist, weight_at_discharge_g: Number(weight) || null, finalize };
      await api.post(`/patients/${patient.id}/discharge`, payload);
      setDirty(false);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    } finally { setSaving(false); }
  }, [f, checklist, weight, patient.id]);

  const [aiCache, setAiCache] = useState(null);       // last AI response (all 8 fields)
  const [aiFieldLoading, setAiFieldLoading] = useState(null); // field key being drafted

  const _fetchAiDraft = useCallback(async () => {
    const variant = type === "lama" ? "lama" : type === "referral" ? "refer" : "discharge";
    const endpoint = variant === "lama"
      ? `/ai/lama-summary/${patient.id}`
      : variant === "refer"
      ? `/ai/refer-summary/${patient.id}`
      : `/ai/discharge-summary/${patient.id}`;
    const { data } = await api.post(endpoint);
    setAiCache(data.draft || {});
    return data.draft || {};
  }, [type, patient.id]);

  const runAiForField = async (fieldKey) => {
    setAiFieldLoading(fieldKey);
    try {
      const draft = aiCache || await _fetchAiDraft();
      const val = (draft[fieldKey] || "").trim();
      if (!val) { toast.info("AI returned nothing for this section — insufficient data"); return; }
      setF((s) => ({ ...s, [fieldKey]: val }));
      setDirty(true);
      toast.success("Section drafted — review before saving");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    finally { setAiFieldLoading(null); }
  };

  const runAiDraft = async (variantOverride) => {
    setDrafting(true);
    try {
      const variant = variantOverride || (type === "lama" ? "lama" : type === "referral" ? "refer" : "discharge");
      const endpoint = variant === "lama"
        ? `/ai/lama-summary/${patient.id}`
        : variant === "refer"
        ? `/ai/refer-summary/${patient.id}`
        : `/ai/discharge-summary/${patient.id}`;
      const { data } = await api.post(endpoint);
      setAiCache(data.draft || {});
      setF((prev) => mergeAiDraft(prev, data.draft || {}));
      setDirty(true);
      const label = variant === "lama" ? "LAMA" : variant === "refer" ? "Referral" : "Discharge";
      toast.success(`AI ${label} draft inserted — review, then save`);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    finally { setDrafting(false); }
  };

  const saveDraft = async () => {
    const ok = await persist({ finalize: false });
    if (ok) toast.success("Draft saved");
  };

  const finalize = async () => {
    if (isRoutine && !ready && !window.confirm("Discharge readiness checklist is incomplete. Finalise anyway?")) return;
    if (type === "lama" && !f.lama_reason) { toast.error("Please enter the LAMA reason before finalising"); return; }
    if (type === "referral" && !f.referral_facility) { toast.error("Please enter the destination facility"); return; }

    // Billing gate: warn/block if balance is due
    if (billing) {
      const bal = Number(billing?.totals?.balance_due || 0);
      const limit = Number(billing?.settings?.outstanding_limit_inr || 0);
      if (bal > 0) {
        const isAdmin = user?.role === "admin";
        if (bal > limit && !isAdmin) {
          toast.error(`Outstanding balance ${inr(bal)} exceeds hospital limit ${inr(limit)}. Ask admin to override or collect payment first.`);
          return;
        }
        const msg = bal > limit
          ? `Outstanding ${inr(bal)} exceeds the limit of ${inr(limit)}. Override and discharge anyway?`
          : `There is an outstanding balance of ${inr(bal)}. Discharge without full payment?`;
        if (!window.confirm(msg)) return;
      }
    }

    const ok = await persist({ finalize: true });
    if (ok) {
      const msg = { routine: "Discharge finalised", lama: "LAMA finalised — patient marked as left against advice", referral: "Referral finalised — patient marked as referred" }[type];
      toast.success(msg);
      // Refresh billing state (bill status may have shifted)
      loadBilling();
      onDischarged?.();
    }
  };

  const { openPdf, printPdf, downloadPdf } = useDischargePdf({
    patientId: patient.id,
    hospitalId: patient.hospital_id,
    dirty,
    persist,
  });

  const finalizeLabel = {
    routine:  patient.status === "discharged" ? "Update discharge" : ready ? "Finalize discharge" : "Finalize anyway",
    lama:     patient.status === "lama" ? "Update LAMA record" : "Finalize LAMA",
    referral: patient.status === "referred" ? "Update referral" : "Finalize referral",
  }[type];

  const previewLabel = { routine: "Preview PDF", lama: "Preview LAMA form", referral: "Preview referral slip" }[type];

  const openInvoicePdf = async (finalize = false) => {
    try {
      const res = await api.get(
        `/billing/bills/${patient.id}/invoice.pdf${finalize ? "?finalize=true" : ""}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (finalize) loadBilling();
    } catch { toast.error("Failed to open invoice"); }
  };

  return (
    <div className="space-y-6" data-testid="discharge-tab">
      {/* Billing gate */}
      {billing && (
        <BillingSummaryCard
          billing={billing}
          role={user?.role}
          onOpenInvoice={openInvoicePdf}
        />
      )}

      {/* Type selector */}
      <div className="card-clinical p-3">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 px-1">Exit type</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="discharge-type-selector">
          {TYPES.map(({ key, label, Icon, accent }) => {
            const active = type === key;
            const activeCls = {
              primary: "border-primary bg-primary/10 text-primary",
              rose:    "border-rose-500 bg-rose-500/10 text-rose-600 dark:text-rose-300",
              amber:   "border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-300",
            }[accent];
            return (
              <button
                key={key}
                type="button"
                onClick={() => changeType(key)}
                data-testid={`disch-type-${key}`}
                className={`rounded-xl border-2 px-4 py-3 flex items-center gap-3 text-sm transition-colors ${
                  active ? activeCls : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
                }`}
              >
                <Icon size={16} />
                <span className="font-medium">{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {isRoutine && <DischargeChecklist checklist={checklist} onToggle={toggleCheck} />}

      {isRoutine && (
        <div className="card-clinical p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Auto-drafted from clinical data</div>
            <p className="text-sm text-muted-foreground mt-1">
              AI variant follows the <b>Exit type</b> selected above ({type === "lama" ? "LAMA" : type === "referral" ? "Refer to higher centre" : "Routine discharge"}).
              Use the <b>Sparkles</b> icon next to any section to draft that section alone, or the button here to fill everything at once.
            </p>
          </div>
          <Button variant="outline"
                  className="rounded-full border-primary/40 text-primary hover:bg-primary/10"
                  onClick={() => runAiDraft()} disabled={drafting} data-testid="disch-ai-draft">
            {drafting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Sparkles size={14} className="mr-2" />}
            {drafting ? "Drafting…" : "Fill all sections with AI"}
          </Button>
        </div>
      )}

      {isRoutine && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ROUTINE_FIELDS.map(([k, l]) => (
            <div key={k} className="card-clinical p-4">
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs uppercase tracking-wider text-muted-foreground">{l}</label>
                <button
                  type="button"
                  onClick={() => runAiForField(k)}
                  disabled={aiFieldLoading === k}
                  title={`AI-draft this section (variant: ${type === "lama" ? "LAMA" : type === "referral" ? "Refer" : "Discharge"})`}
                  className="h-6 w-6 inline-flex items-center justify-center rounded-full text-muted-foreground hover:bg-primary/10 hover:text-primary"
                  data-testid={`disch-ai-field-${k}`}
                >
                  {aiFieldLoading === k ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                </button>
              </div>
              <Textarea rows={4} value={f[k] || ""} onChange={(e) => updateField(k, e.target.value)} data-testid={`disch-${k}`} className="mt-2" />
            </div>
          ))}
          <div className="card-clinical p-4">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Weight at discharge (g)</label>
            <Input type="number" value={weight} onChange={(e) => { setWeight(e.target.value); setDirty(true); }} className="mt-2" />
          </div>
        </div>
      )}

      {type === "lama" && <LamaForm f={f} updateField={updateField} />}
      {type === "referral" && <ReferralForm f={f} updateField={updateField} patientId={patient.id} />}

      <div className="sticky bottom-4 flex flex-wrap justify-end gap-3 bg-background/85 backdrop-blur border border-border rounded-xl p-3">
        <div className="text-xs text-muted-foreground self-center mr-auto">
          {dirty ? "Unsaved changes — will save automatically before printing." : "All changes saved."}
        </div>
        <Button variant="outline" onClick={saveDraft} disabled={saving} className="rounded-full" data-testid="disch-save-draft">
          <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save draft"}
        </Button>
        <Button variant="outline" onClick={openPdf} className="rounded-full" data-testid="disch-pdf">
          <FileDown size={14} className="mr-2" /> {previewLabel}
        </Button>
        <Button variant="outline" onClick={downloadPdf} className="rounded-full" data-testid="disch-download">
          <FileDown size={14} className="mr-2" /> Download
        </Button>
        <Button variant="outline" onClick={printPdf} className="rounded-full" data-testid="disch-print">
          <Printer size={14} className="mr-2" /> Print
        </Button>
        <Button onClick={finalize} disabled={saving}
                className={`rounded-full ${
                  type === "lama" ? "bg-rose-500 hover:bg-rose-600" :
                  type === "referral" ? "bg-amber-500 hover:bg-amber-600" :
                  (!ready ? "bg-amber-500 hover:bg-amber-600" : "")
                }`}
                data-testid="disch-save">
          {saving ? "Saving…" : finalizeLabel}
        </Button>
      </div>
      {isRoutine && !ready && (
        <div className="text-xs text-amber-700 dark:text-amber-300 text-right">
          Checklist is optional — completing it produces a safer discharge record and will surface every item on the printed PDF.
        </div>
      )}
    </div>
  );
}

function BillingSummaryCard({ billing, role, onOpenInvoice }) {
  const bal = Number(billing?.totals?.balance_due || 0);
  const paid = Number((billing?.totals?.advance_total || 0) + (billing?.totals?.paid_total || 0) - (billing?.totals?.refund_total || 0));
  const limit = Number(billing?.settings?.outstanding_limit_inr || 0);
  const isClosed = billing?.bill?.status === "closed";
  const overLimit = bal > limit;
  const tone =
    bal <= 0 ? "border-emerald-500/50 bg-emerald-500/5"
    : overLimit ? "border-destructive/60 bg-destructive/10"
    : "border-amber-500/60 bg-amber-500/10";
  return (
    <div className={`rounded-xl border p-4 flex flex-wrap items-center justify-between gap-4 ${tone}`} data-testid="discharge-billing-card">
      <div className="flex items-center gap-3">
        <Wallet size={18} className={bal <= 0 ? "text-emerald-500" : overLimit ? "text-destructive" : "text-amber-500"} />
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Billing summary</div>
          <div className="text-sm mt-0.5">
            <span className="text-muted-foreground">Net </span><b>{inr(billing.totals.net_payable)}</b>
            <span className="text-muted-foreground"> · Received </span><b>{inr(paid)}</b>
            <span className="text-muted-foreground"> · Balance </span>
            <b className={bal <= 0 ? "text-emerald-600 dark:text-emerald-400" : overLimit ? "text-destructive" : "text-amber-600 dark:text-amber-300"}>{inr(bal)}</b>
          </div>
          {overLimit && (
            <div className="text-xs mt-1 text-destructive">
              Exceeds hospital outstanding limit ({inr(limit)}). {role === "admin" ? "Admin can override at finalize." : "Ask admin or collect balance first."}
            </div>
          )}
          {isClosed && billing?.bill?.invoice_no && (
            <div className="text-xs mt-1 text-muted-foreground">Invoice # {billing.bill.invoice_no}</div>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => onOpenInvoice(false)} data-testid="disch-preview-invoice">
          <FileDown size={13} className="mr-1.5" /> {isClosed ? "Print invoice" : "Preview invoice"}
        </Button>
        {(role === "admin" || role === "receptionist" || role === "doctor" || role === "nurse") && !isClosed && (
          <Button size="sm" className="rounded-full" onClick={() => onOpenInvoice(true)} data-testid="disch-finalize-invoice">
            Finalize &amp; print
          </Button>
        )}
      </div>
    </div>
  );
}
